import React, {FunctionComponent, useEffect, useState} from 'react';
import superHostKey from 'assets/svg/superhost-user-key-indicator-for-avatar.svg';
import videoOutlineIcon from 'assets/svg/video-outline.svg';
import chatOutlineIcon from 'assets/svg/chat_outline.svg';
import joinIcon from 'assets/svg/join button.svg';
import extendedRoomIcon from 'assets/svg/extended-room.svg';
import { useHistory } from 'react-router';
import Styles from './NearbyItem.module.scss';
import ProfileAvatar from 'components/profileAvatar/ProfileAvatar';
import { beginVideoCall, sendJoinRequest, useGetRooms, ensureUserInPublicRoom } from 'store/services/firebase-broadcast';
import api from 'store/services/conversation.service';
import { generateMeetingLink } from 'utils/meetingLinkUtils';
import { toast } from 'react-toastify';
import { getCurrentDate, getParticipantsArray, getParticipantsCount, randomString, userIsRoomMember, isPublicRoom } from 'helpers/utils';
import { formatUserDateTimeCached } from 'helpers/dateTimeFormat';

interface NearbyItemProps {
  item: any;
  conversations: any;
  handleExpand: (e: React.FormEvent<HTMLButtonElement>) => void;
  joinRequest: Function;
  isJoined: boolean;
  roomWithFees: Function;
  roomWithNoFees: Function;
  joinRoom: Function;
  profile: Function;
  setJoinIntent: (intent: 'video' | 'chat') => void;
}

const NearbyItem: FunctionComponent<NearbyItemProps> = (props: NearbyItemProps) => {
  const { handleExpand, item, roomWithFees, roomWithNoFees, joinRoom, profile, setJoinIntent } = props;
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [joined, setJoined] = useState<boolean>(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const host = item.host;

  const allRooms = useGetRooms();
  const firebaseRoom = allRooms.find((r: any) => r.pk === item.pk);
  const roomData = firebaseRoom || item;
  const participants = getParticipantsArray(roomData.participants);
  const participantCount = getParticipantsCount(roomData.participants);
  const roomStatus = roomData?.status || item?.status || 'CHAT';
  const isHost = host.pk === user.pk;
  const currentUser = participants.find((p: any) => p?.pk === user.pk) ?? user;
  const showJoinedUserAvatar = joined && !isHost;
  const participantWord = participantCount === 1 ? 'participant' : 'participants';

  const handleViewProfile = (person: any) => {
    profile(person, roomData.pk);
  };

  const navigateToVideoCall = async () => {
    const status = roomData?.status || item?.status || 'CHAT';
    const isStartingNewVideo = isHost && status !== 'VIDEO';

    if (isStartingNewVideo) {
      if (roomData?.participants?.length > 0) {
        const emails: string[] = [];
        for (const participant of roomData.participants) {
          participant?.email && emails.push(participant.email);
        }

        if (emails.length > 0) {
          try {
            const meetingLink = generateMeetingLink(roomData, window.location.origin);
            await api.sendMeetingNotificationEmail({
              emailTo: emails.join(','),
              notification: `The video has been started by the Host (${user.name}) for the room ${roomData.topic}.

Join the call directly: ${meetingLink}

Room: ${roomData.topic}
Host: ${user.name}
Start Time: ${formatUserDateTimeCached(new Date()) || new Date().toISOString()}

Click the link above to join the video call immediately.`,
              subject: `${roomData.topic}'s video started by host - Join Now!`,
            });
          } catch (error) {
            console.error('Failed to send meeting notification email:', error);
          }
        }
      }

      await api.roomStatusRequest({ id: roomData.pk, status: 'VIDEO' });
      await beginVideoCall(roomData.pk);
    }

    if (!isHost && !roomData?.call_active) {
      toast.error('The host has not started the video call yet.');
      return;
    }

    history.push({
      pathname: '/call',
      state: {
        selectedBroadCast: roomData,
        isStartingVideo: isStartingNewVideo,
        requireCallActive: !isHost,
      },
    });
  };

  const openJoinModal = (intent: 'video' | 'chat', room: any, feeType: 'gold' | 'superHost') => {
    document.body.classList.add('modal-open');
    setJoinIntent(intent);
    joinRoom(room);
    if (feeType === 'gold') {
      roomWithFees(true);
    } else {
      roomWithNoFees(true);
    }
  };

  const submitChatJoinRequest = () => {
    const request = {
      roomId: roomData.pk,
      id: `${roomData.pk}-${randomString()}`,
      user,
      time: getCurrentDate(),
      timestamp: Date.now(),
      roomTopic: roomData.topic,
    };

    sendJoinRequest(request)
      .then(() => {
        toast.success('Join request has been sent');
        history.push({ pathname: '/chat-room', state: { selected: roomData, preserveSelection: true } });
      })
      .catch((error: { code?: string }) => {
        if (error.code === 'INVALID_USER_DATA') {
          return;
        }
        if (error.code === 'DUPLICATE_REQUEST') {
          toast.warning('You already have a pending request for this room. Please wait for the host to respond.');
          return;
        }
        toast.error('Something went wrong!');
      });
  };

  const handleJoin = async () => {
    if (isVideoLoading) {
      return;
    }

    setIsVideoLoading(true);
    try {
      // Public rooms: enter without host approval / fee modals.
      if (isPublicRoom(roomData, allRooms)) {
        if (!joined) {
          await ensureUserInPublicRoom(roomData, user, allRooms);
          setJoined(true);
        }
        await navigateToVideoCall();
        return;
      }

      if (item.host.is_super_host) {
        if (joined) {
          await navigateToVideoCall();
        } else {
          openJoinModal('video', roomData, 'superHost');
        }
      } else if (joined) {
        await navigateToVideoCall();
      } else if (participants.length >= 3) {
        openJoinModal('video', roomData, 'gold');
      } else {
        await navigateToVideoCall();
      }
    } catch (error) {
      console.error('Error starting video from nearby:', error);
      toast.error('Unable to start the video call. Please try again.');
    } finally {
      setIsVideoLoading(false);
    }
  };

  useEffect(() => {
    setJoined(userIsRoomMember(roomData, user?.pk));
  }, [roomData, user?.pk, participants.length]);

  const chatBtn = () => {
    history.push({ pathname: '/chat-room', state: { selected: roomData, preserveSelection: true } });
  };

  const handleChatJoin = () => {
    if (joined) {
      chatBtn();
      return;
    }

    // Public rooms: open chat immediately; panel auto-joins on enter.
    if (isPublicRoom(roomData, allRooms)) {
      chatBtn();
      return;
    }

    if (item.host.is_super_host) {
      openJoinModal('chat', roomData, 'superHost');
      return;
    }

    if (participants.length >= 3) {
      openJoinModal('chat', roomData, 'gold');
      return;
    }

    submitChatJoinRequest();
  };

  const handleExtendedRoom = () => {
    handleExpand({ preventDefault: () => {} } as React.FormEvent<HTMLButtonElement>);
  };

  return (
    <div className={Styles.itemWrapper}>
      <div className={Styles.cardHeader}>
        <span className={Styles.topicText}>{item.topic}</span>
      </div>

      <div className={Styles.cardMiddle}>
        <div className={Styles.hostSection}>
          <div
            className={Styles.avatarWrapper}
            onClick={() => handleViewProfile(host)}
            role="button"
            tabIndex={0}
            onKeyPress={() => handleViewProfile(host)}
          >
            <ProfileAvatar
              profile={host.profile}
              alt={host.name}
              className={Styles.hostAvatar}
            />
            {host.is_super_host && (
              <img src={superHostKey} alt="super host" className={Styles.superHostKey} />
            )}
          </div>
          <h5 className={Styles.hostName}>{host.name}</h5>
          {host.is_super_host ? (
            <span className={Styles.superHostLabel}>SUPER HOST</span>
          ) : (
            <span className={Styles.hostLabel}>HOST</span>
          )}
        </div>

        <div className={Styles.columnDivider} aria-hidden="true" />

        <div className={Styles.participantSummary}>
          {showJoinedUserAvatar && (
            <div className={Styles.currentUserBlock}>
              <ProfileAvatar
                profile={currentUser.profile}
                alt={currentUser.name}
                className={Styles.summaryAvatar}
              />
              <span className={Styles.summaryUserName}>{currentUser.name}</span>
            </div>
          )}
          <div className={Styles.participantCount}>
            <span className={Styles.participantCountLine}>
              <span className={Styles.participantCountNumber}>{participantCount}</span>
              {' '}{participantWord}
            </span>
            <span className={Styles.participantCountSubline}>in the Room</span>
          </div>
        </div>

        <div className={Styles.divider} />

        <div className={Styles.participantsList}>
          {participants.slice(0, 5).map((participant: any, index: number) => (
            <div
              key={index}
              className={Styles.participantItem}
              onClick={() => handleViewProfile(participant)}
              role="button"
              tabIndex={0}
              onKeyPress={() => handleViewProfile(participant)}
            >
              <ProfileAvatar
                profile={participant.profile}
                alt={participant.name}
                className={Styles.participantAvatar}
              />
              <span className={Styles.participantName}>{participant.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={Styles.divider} aria-hidden="true" />

      <div className={Styles.btnArea}>
        {isPublicRoom(roomData, allRooms) && (
          <p className={Styles.openRoomLine}>Open to everyone</p>
        )}
        <div className={Styles.actionRow}>
          {host.pk === user.pk && (
            <div
              className={Styles.actionIconBtn}
              onClick={handleJoin}
              onKeyUp={handleJoin}
              role="button"
              tabIndex={0}
            >
              <img src={videoOutlineIcon} alt={roomStatus === 'VIDEO' ? 'Join video call' : 'Start video call'} className={`${Styles.actionIconImg} ${Styles.actionIconImgVideo}`} />
            </div>
          )}

          {host.pk !== user.pk && (
            roomStatus === 'VIDEO' && roomData?.call_active === true ? (
              <div
                className={Styles.actionIconBtn}
                onClick={handleJoin}
                onKeyUp={handleJoin}
                role="button"
                tabIndex={0}
              >
                <img src={videoOutlineIcon} alt="Join video call" className={`${Styles.actionIconImg} ${Styles.actionIconImgVideo}`} />
              </div>
            ) : (
              <div
                className={Styles.actionIconBtn}
                onClick={handleChatJoin}
                onKeyDown={(e) => e.key === 'Enter' && handleChatJoin()}
                role="button"
                tabIndex={0}
              >
                <img
                  src={joined ? chatOutlineIcon : joinIcon}
                  alt={joined ? 'Open chat' : 'Join room'}
                  className={`${Styles.actionIconImg} ${joined ? Styles.actionIconImgChat : Styles.actionIconImgJoin}`}
                />
              </div>
            )
          )}

          {participants.length > 0 && (
            <div
              className={Styles.actionIconBtn}
              onClick={handleExtendedRoom}
              onKeyDown={(e) => e.key === 'Enter' && handleExtendedRoom()}
              role="button"
              tabIndex={0}
              aria-label="Extended room"
            >
              <img src={extendedRoomIcon} alt="" className={`${Styles.actionIconImg} ${Styles.actionIconImgExtended}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NearbyItem;
